import { firebaseServiceModule } from '../firebase/firebase';
import * as EventUtils from '../../utils/event-utils';
import { encodeForFirebaseRef } from '../../utils/string-utils';
import { idEquals } from '../../utils/object-utils/object-utils';
import { isObject } from '../../utils/type-utils';
import { solutionModelModule } from '../../models/solution';

// If the current editor pauses longer than this, we allow others to take over editing silently
const INTERACT_EDITOR_TIMEOUT_MS = 2000;

// firebase data older than 5 minutes is considered stale, and we ignore it
const INTERACT_STALE_DATA_TIMEOUT_MS = 5 * 60 * 1000;

export const INTERACT_STATUS_CONNECTING = 'connecting';
export const INTERACT_STATUS_CONNECTED = 'connected';
export const INTERACT_STATUS_DISCONNECTED = 'disconnected';

const activeFileIdKey = 'activeFileId';
const activeFileKey = 'activeFile';
const activeEditorIdKey = 'activeEditorId';
const activeEditorKey = 'activeEditor';
const savedVersionKey = 'savedVersion';
const editedVersionKey = 'editedVersion';
const fileActionKey = 'fileAction';

const FILE_ACTION_DELETE = 'Deleted';
const FILE_ACTION_RENAME = 'Renamed';
const FILE_ACTION_CREATE = 'Created';
const MULTIPLE_FILES_CHANGED_ACTION = 'sChanged';

const _ON_DISCONNECT = {
  toDestroy: [activeFileKey],
  toNull: [activeFileIdKey, activeFileKey, activeEditorIdKey, activeEditorKey],
};

export const solutionInteractServiceModule = angular
  .module('Q.services.solution-interact-service', [
    firebaseServiceModule,
    solutionModelModule,
  ])

  .factory(
    'solutionInteractService',
    function (firebase, $q, $log, $timeout, Solution) {
      return {
        /**
         * Starts Interact on the provided service.
         *
         * @param {string} challengeRef - Firebase ref for this challenge
         * @param {Object} liveInterview - Object returned from liveInterviewService.join
         * @param {String} solutionId - ID of Solution being interacted with
         */
        start(challengeRef, liveInterview, solutionId) {
          return _startInteract(
            firebase,
            $q,
            $timeout,
            $log,
            Solution,
            challengeRef,
            liveInterview,
            solutionId,
          );
        },
      };
    },
  );

/**
 * Sets up an Interact service.
 *
 * This service is designed around a one-editor-at-a-time workflow.
 * Each user can take over editing at any time, and while they are editing, all other users are assumed to be in
 * read-only mode. This prevents the complexity of tracking multiple users editing simultaneously, which isn't
 * really that useful in practice.
 *
 * The service starts by connecting to and watching two primary properties: activeFileId and activeEditorId.
 * Based on these, we can decide when to open (and/or close) files, and who has editing access.
 *
 * If a file is modified via the updateFile method, we assume the current user is now the editor, and update the
 * appropriate fields.
 *
 * Users of this service should rely on the built-in event emitter to handle changes.
 *
 * Whenever the solution is saved, the methods markSaving and markSaved should be called, to ensure we can identify
 * when it's safe to transfer editor control.
 *
 * Events:
 * - connected - we're successfully connected
 * - connectionError<{ response }> - if something went wrong while connecting
 * - disconnected - we've successfully disconnected
 * - activeFileIdChanged<{ activeFileId }> - the active file has changed (could be null)
 * - activeEditorChanged<{ activeEditorId, activeEditor }> - the active editor has changed (could be null)
 * - fileChanged<{ id, properties }> - a file has been modified (also called when first opened if not stale)
 * - savedVersionChanged<{ savedVersion }> - The saved version has been incremented (may or may not mean that the challenge is saved to our server)
 * - editedVersionChanged<{ editedVersion }> - The edited version has been incremented, likely means the challenge is no longer in-sync with the server
 * - fileCreated<{ id, properties }> - a file has been created
 * - fileRenamed<{ oldPath, newPath }> - a file has been renamed
 * - fileDeleted<{ id }> - a file has been deleted
 * - filesChanged<{ ids }> - several files have been changed at once
 *
 * Note: For file change events, it's assumed the visible file will be tracked via activeFileIdChanged events, since it's
 * possible a file is opened for editing, but the contents are stale or unmodified
 *
 *
 * @param {Object }firebase - Angular Firebase library
 * @param {Promise} $q - Angular Promise Library
 * @param {Object} $timeout - Angular $timeout library
 * @param {Object} $log - Angular $log library
 * @param {Solution} Solution - Solution model
 * @param {string} challengeRef - Base reference for the Firebase properties
 * @param {Object} liveInterview - LiveInterviewService instance
 * @param {String} solutionId - ID of Solution being interacted with
 * @return {Object} The Interact Service
 * @private
 */
function _startInteract(
  firebase,
  $q,
  $timeout,
  $log,
  Solution,
  challengeRef,
  liveInterview,
  solutionId,
) {
  // will get appended with the file ID, allows us to track the file contents
  const filesRef = challengeRef + '/files';
  const interactRef = challengeRef + '/interact';
  const activeFileIdRef = interactRef + '/' + activeFileIdKey;
  const activeEditorIdRef = interactRef + '/' + activeEditorIdKey;
  const savedVersionRef = interactRef + '/' + savedVersionKey;
  const editedVersionRef = interactRef + '/' + editedVersionKey;
  const fileActionRef = interactRef + '/' + fileActionKey;
  const getFileRef = (id) => `${filesRef}/${encodeForFirebaseRef(id)}`;

  const unwatch = {};

  const revisionsCount = () => {
    const solution = solutionId && Solution.get(solutionId);
    return (solution && solution.revisionsCount) || 0;
  };

  let saving = false;
  let editedWhileSaving = false;

  let fileChangeTimer;

  const svc = {
    status: INTERACT_STATUS_DISCONNECTED,
    isDisconnected: true,
    isConnecting: false,
    isConnected: false,

    // A promise that resolves when the service is connected, or rejects if there was an error
    initializationPromise: null,

    // Firebase values
    activeFileId: null,
    activeFile: null,
    activeEditorId: null,
    activeEditor: null,
    savedVersion: revisionsCount(),
    editedVersion: revisionsCount(),

    // Utility booleans automatically updated when data changes
    isBeingEdited: false,
    isBeingEditedByOthers: false,
    isUserActiveEditor: false,
    hasOtherEditor: false,
    isSaved: true,
    canEdit: true,

    lastEdited: 0,

    buttonTooltip: '',
    baseButtonClasses: '',
    buttonClasses(fileId) {
      let classes = svc.baseButtonClasses;
      if (svc.isBeingEdited && (!fileId || svc.activeFileId === fileId)) {
        classes += ' being-edited';
        if (svc.isBeingEditedByOthers) {
          classes += ' being-edited-by-others';
        }
      }
      return classes;
    },

    /**
     * Call whenever the user is the active editor and modifies a file.
     *
     * @param {*} file
     * @param {string} file.id - ID of file being tracked
     * @param {Object} file.properties - properties of file to track (should be a simple hash)
     */
    updateFile({ id = svc.activeFileId, properties } = {}) {
      if (!id || svc.isDisconnected) return;

      _setActiveEditor();

      svc.activeFileId = id;
      firebase.set(activeFileIdRef, id);

      if (properties !== undefined) {
        // update edited version if necessary. Increment by 2 if we're mid-save.
        if (saving) {
          editedWhileSaving = true;
        }
        firebase.set(editedVersionRef, svc.savedVersion + 1);
        const oldProps = (svc.activeFile && svc.activeFile.properties) || {};
        firebase.set(getFileRef(id), {
          lastEdited: svc.lastEdited,
          properties: angular.extend({}, oldProps, properties),
        });
      }
      _updateService();
    },

    /**
     * Updates many files at once.
     * @param {Array<{id:string, properties:*}>} files
     */
    updateFiles(files) {
      if (!files || !files.length || svc.isDisconnected) return;

      _setActiveEditor();

      const ids = [];

      files.forEach(({ id, properties }) => {
        if (properties !== undefined) {
          // update edited version if necessary. Increment by 2 if we're mid-save.
          if (saving) {
            editedWhileSaving = true;
          }
          firebase.set(editedVersionRef, svc.savedVersion + 1);
          firebase.set(getFileRef(id), {
            lastEdited: svc.lastEdited,
            properties: angular.extend(
              { contents: '', selections: [] },
              properties,
            ),
          });
          ids.push(id);
        }
      });

      if (ids.length) {
        firebase.set(fileActionRef, {
          action: MULTIPLE_FILES_CHANGED_ACTION,
          ids,
        });
      }
    },

    /**
     * Looks up the Interact contents of a file
     * @param {string} id - ID of file to get
     * @returns {Promise<{id:string, properties:*}>} Resolves with the file contents
     */
    getFile({ id }) {
      if (svc.disconnected) return $q.resolve();
      return firebase.get(getFileRef(id));
    },

    /**
     * Notify that a file has been created
     * @param {string} id - new file ID
     * @param {*?} properties - new file properties
     */
    createFile({ id, properties }) {
      if (!id || svc.isDisconnected) return;
      _setActiveEditor();
      firebase.set(fileActionRef, {
        action: FILE_ACTION_CREATE,
        id,
        properties,
      });
    },

    /**
     * Notify that a file has been renamed
     * @param {string?} id - optional file ID
     * @param {string} oldPath - old file path
     * @param {string} newPath - new file path
     */
    renameFile({ id, oldPath, newPath }) {
      if (!oldPath || svc.isDisconnected) return;
      _setActiveEditor();
      firebase.set(fileActionRef, {
        action: FILE_ACTION_RENAME,
        id,
        oldPath,
        newPath,
      });
    },

    deleteFile({ id }) {
      if (!id || svc.isDisconnected) return;
      _setActiveEditor();
      firebase.set(fileActionRef, { action: FILE_ACTION_DELETE, id });
    },

    /**
     * Call when saving the current model, to track if any edits happen while saving.
     */
    markSaving() {
      if (svc.isUserActiveEditor) {
        saving = true;
      }
    },

    /**
     * Call whenever the solution is saved. Increments the savedVersion so we can track if the changes are in-sync.
     */
    markSaved() {
      if (svc.isUserActiveEditor) {
        svc.savedVersion = revisionsCount();
        // Update edited version. If we edited While saving, we bump it up again
        firebase.set(
          editedVersionRef,
          svc.savedVersion + (editedWhileSaving ? 1 : 0),
        );
        // Store the savedVersion second, because we want to note that we haven't saved yet
        firebase.set(savedVersionRef, svc.savedVersion);
        _updateService();
      }
      saving = editedWhileSaving = false;
    },

    /**
     * Can be used to reconnect this service if disconnected, also used when created to set up
     * the initial connections.
     *
     * @return {Promise} That resolves when everything is ready
     */
    reconnect() {
      if (!svc.isDisconnected) return;

      // start the last edit timer now, in case we come in mid-edit
      svc.lastEdited = Date.now();
      _startFileChangeTimer();

      _setStatus(INTERACT_STATUS_CONNECTING);
      return (svc.initializationPromise = $q
        .all([
          _setupValueWatcher(activeFileIdRef, _handleActiveFileIdChange),
          _setupValueWatcher(activeEditorIdRef, _handleActiveEditorIdChange),
          _setupValueWatcher(
            savedVersionRef,
            _handleVersionChange.bind(savedVersionRef, savedVersionKey),
          ),
          _setupValueWatcher(
            editedVersionRef,
            _handleVersionChange.bind(editedVersionRef, editedVersionKey),
          ),
          firebase.get(fileActionRef).then((fileAction) => {
            if (svc.isDisconnected) return;
            // we only respond to changes to file actions, ignoring it on startup
            unwatch[fileActionRef] = fileAction.$watch(() =>
              _handleFileActionChange(fileAction),
            );
          }),
        ])
        .then(() => {
          if (svc.isDisconnected) return;
          _setStatus(INTERACT_STATUS_CONNECTED);
          svc.emit('connected', { interact: svc });
          return svc;
        })
        .catch((response) => {
          // reset everything if something went wrong
          _disconnect();
          svc.emit('connectionError', { interact: svc, response });
          return $q.reject(response);
        }));
    },

    /**
     * Disconnects and destroys any connections for this instance.
     */
    disconnect() {
      if (svc.isDisconnected) return;
      _disconnect();
      svc.emit('disconnected', { interact: svc });
    },

    /**
     * Completely destroys this Interact session
     */
    destroy() {
      _disconnect();
      svc.off(true);
    },
  };

  // enable events on the service
  EventUtils.eventEmitter(svc);

  // update our active editor in case the user list was out-of-sync with our current editor ID
  liveInterview.on('usersListChanged', () => _handleActiveEditorIdChange());

  // start the service
  svc.reconnect();

  return svc;

  //// Private Internal Methods

  function _setActiveEditor() {
    if (svc.activeEditorId !== liveInterview.userProfile.id) {
      _handleActiveEditorIdChange(liveInterview.userProfile.id);
      firebase.set(activeEditorIdRef, svc.activeEditorId);
    }

    svc.lastEdited = Date.now();
    _startFileChangeTimer();
    _updateService();
  }

  /**
   * Sets up a Firebase watcher for the value reference
   * @param {string} ref - Firebase reference
   * @param {Function} handler - If provided, handles changes to the value, otherwise it's just copied to the service
   * @return {Promise}
   * @private
   */
  function _setupValueWatcher(ref, handler) {
    return firebase.get(ref).then((obj) => {
      if (svc.isDisconnected) return;
      handler(obj.$value);
      unwatch[ref] = obj.$watch(() => handler(obj.$value));
    });
  }

  /**
   * Clears all properties, destroys any watchers, and updates the service variables
   * @private
   */
  function _disconnect() {
    _setStatus(INTERACT_STATUS_DISCONNECTED);
    angular.forEach(unwatch, (uw, k) => {
      uw();
      delete unwatch[k];
    });

    const users = liveInterview.usersList || [];
    // if nobody is left on the interview, clean up
    if (users.length === 0 || (users.length === 1 && users[0].$self)) {
      firebase.remove(interactRef);
      firebase.remove(filesRef);
    }

    _ON_DISCONNECT.toDestroy.forEach((k) => {
      svc[k] && svc[k].$destroy();
    });
    _ON_DISCONNECT.toNull.forEach((k) => {
      svc[k] = null;
    });
    _updateService();
  }

  /**
   * Sets the service status and updates any service variables
   * @param status
   * @private
   */
  function _setStatus(status) {
    svc.status = status;
    svc.isConnecting = status === INTERACT_STATUS_CONNECTING;
    svc.isConnected = status === INTERACT_STATUS_CONNECTED;
    svc.isDisconnected = status === INTERACT_STATUS_DISCONNECTED;
    _updateService();
  }

  /**
   * Sets up utility booleans based on current service status
   * @private
   */
  function _updateService() {
    const editor = svc.isConnected && svc.activeEditor;
    svc.isUserActiveEditor = !!editor && editor.$self;
    svc.hasOtherEditor = !!editor && !editor.$self;
    svc.isBeingEdited =
      !!editor && Date.now() - svc.lastEdited < INTERACT_EDITOR_TIMEOUT_MS;
    svc.isBeingEditedByOthers = svc.hasOtherEditor && svc.isBeingEdited;
    // TODO: how to handle dropoffs? what happens if the current editor is unable to save changes?
    svc.isSaved = svc.savedVersion >= svc.editedVersion;
    const couldEdit = svc.canEdit;
    svc.canEdit = svc.isUserActiveEditor || (!svc.isBeingEdited && svc.isSaved);
    svc.cannotEdit = !svc.canEdit;

    _updateButtonTooltip();
    _updateBaseButtonClasses();

    if (svc.canEdit !== couldEdit) {
      svc.emit('canEditChanged', { interact: svc, canEdit: svc.canEdit });
    }
  }

  /**
   * Updates the button tooltip text based on the current service state
   * @private
   */
  function _updateButtonTooltip() {
    let tooltip =
      'Nobody is currently editing the solution, you can edit at any time.';
    if (svc.isConnecting) {
      tooltip = 'Connecting to Interact service';
    } else if (svc.isDisconnected) {
      tooltip = 'Unable to connect to Interact service. Click to try again';
    } else if (svc.isBeingEditedByOthers) {
      const who = svc.activeEditor ? svc.activeEditor.name : 'someone else';
      const clickAction = svc.canEdit
        ? 'Click to take control.'
        : 'Please wait while changes are synced.';
      tooltip = `The solution is being actively edited by ${who}. ${clickAction}`;
    } else if (svc.isBeingEdited) {
      tooltip = 'You are currently editing the solution.';
    } else if (svc.activeEditor) {
      if (svc.activeEditor.$self) {
        tooltip += ' (You were the last editor.)';
      } else {
        tooltip += ` (${svc.activeEditor.name} was the last editor.)`;
      }
    }
    svc.buttonTooltip = tooltip;
  }

  /**
   * Updates the base button classes based on the current service state
   * @private
   */
  function _updateBaseButtonClasses() {
    let classes = svc.activeEditor ? 'has-active-editor' : 'no-active-editor';
    if (svc.isConnecting) {
      classes += ' connecting';
    } else if (svc.isDisconnected) {
      classes += ' disconnected';
    } else if (svc.isBeingEdited) {
      classes += svc.canEdit ? ' can-edit' : ' syncing';
    }
    svc.baseButtonClasses = classes;
  }

  /**
   * Handles a version change. We assume that versions only increment,
   * so if our version is higher (and we're the active editor), we override the version stored on the server.
   * @param {string} ref - firebase ref
   * @param {string} key - service key
   * @param {number} version - new version from Firebase
   * @private
   */
  function _handleVersionChange(ref, key, version) {
    if (version && svc[key] < version) {
      svc[key] = version;
      _updateService();
      svc.emit(`${key}Changed`, { interact: svc, [key]: version });
    } else if (svc.isUserActiveEditor && svc[key] !== version) {
      firebase.set(ref, svc[key]);
    }
  }

  /**
   * When the active file is changed, we need to update our watcher, and send out events.
   * @param {string} activeFileId - The new active file ID.
   * @private
   */
  function _handleActiveFileIdChange(activeFileId) {
    if (svc.activeFileId !== activeFileId) {
      svc.activeFileId = activeFileId || null;
      _updateService();
      svc.emit('activeFileIdChanged', { interact: svc, activeFileId });
      _watchFile();
    }
  }

  /**
   * Updates the active editor. Looks up the active editor in the liveInterview usersList.
   *
   * @param {string?} activeEditorId - When provided, sets the active Editor ID
   * @private
   */
  function _handleActiveEditorIdChange(activeEditorId = svc.activeEditorId) {
    svc.activeEditorId = activeEditorId || null;
    let activeEditor =
      liveInterview.usersList.find((u) => u.id === activeEditorId) || null;
    // clear old editor on connect
    if (svc.isConnecting && activeEditor && activeEditor.$self) {
      svc.activeEditorId = activeEditor = null;
      firebase.set(activeEditorIdRef, '');
      firebase.set(activeFileIdRef, '');
      firebase.set(fileActionRef, {});
    }
    if (!idEquals(activeEditor, svc.activeEditor)) {
      svc.activeEditor = activeEditor;
      _updateService();
      svc.emit('activeEditorChanged', {
        interact: svc,
        activeEditorId,
        activeEditor,
      });
    }
    // switch file watching if necessary
    _watchFile();
  }

  /**
   * Handles changes to the file action object. Supported actions: Created, Renamed, Deleted
   *
   * Simply emits a unique event when an action occurs.
   *
   * @param {{ action: string, id: string?, properties: object?, oldPath: string?, newPath: string? }} fileAction
   * @private
   */
  function _handleFileActionChange(fileAction) {
    if (!svc.isUserActiveEditor && isObject(fileAction)) {
      const { action, id, ids, properties, oldPath, newPath } = fileAction;
      svc.emit('file' + action, {
        interact: svc,
        id,
        ids,
        properties,
        oldPath,
        newPath,
      });
    }
  }

  /**
   * Stops watching the current active file
   * @private
   */
  function _unwatchFile() {
    if (unwatch.activeFile) {
      unwatch.activeFile();
      delete unwatch.activeFile;
      if (svc.activeFile) {
        svc.activeFile.$destroy();
      }
      svc.activeFile = null;
    }
  }

  /**
   * Watches the active file. At first, we have to look up the file, which we immediately treat as an edit.
   * Then we watch for changes.
   *
   * If the current file matches the current, actively watched file, we do nothing.
   *
   * If there is no active file ID, we clear everything and exit.
   *
   * @private
   */
  function _watchFile() {
    if (
      unwatch.activeFile &&
      svc.activeFile &&
      svc.activeFile.id === svc.activeFileId
    ) {
      // no need to watch, already watching
      return;
    }
    if (!svc.activeFileId) {
      _unwatchFile();
      return;
    }
    firebase
      .get(getFileRef(svc.activeFileId))
      .then((activeFileRef) => {
        _unwatchFile();
        svc.activeFile = activeFileRef;
        _handleEdit();
        unwatch.activeFile = activeFileRef.$watch(() => _handleEdit());
      })
      .catch((err) => {
        $log.error('Unable to watch file for Interact', err);
        // unwatch the previous file if something went wrong, and clear any values
        _unwatchFile();
        svc.activeFileId = null;
      });
  }

  /**
   * Handles changes to the actively watched file
   *
   * If the user is the active editor, we ignore the changes.
   * If the active file exists, but has not been edited in a long while (is stale), we ignore the changes.
   * Otherwise, we emit a change event.
   *
   * @private
   */
  function _handleEdit(force) {
    if (
      !svc.isUserActiveEditor &&
      svc.activeFile &&
      (force ||
        Date.now() - svc.activeFile.lastEdited < INTERACT_STALE_DATA_TIMEOUT_MS)
    ) {
      svc.lastEdited = Date.now();
      _startFileChangeTimer();
      const properties = {};
      angular.forEach(svc.activeFile.properties || {}, (v, k) => {
        if (k[0] !== '$') {
          properties[k] = v;
        }
      });
      if (Object.keys(properties).length) {
        svc.emit('fileChanged', {
          interact: svc,
          id: svc.activeFileId,
          properties,
        });
      }
    }
    _updateService();
  }

  /**
   * After the editor timer runs out, we update our service booleans
   */
  function _startFileChangeTimer() {
    $timeout.cancel(fileChangeTimer);
    fileChangeTimer = $timeout(() => {
      _updateService();
      svc.emit('activeEditorChanged', {
        interact: svc,
        activeEditorId: svc.activeEditorId,
        activeEditor: svc.activeEditor,
      });
    }, INTERACT_EDITOR_TIMEOUT_MS);
  }
}
